import { useMemo } from 'react'
import { adminMenuKeyForNavPath, canEditAdminMenu, canViewAdminMenu, type AdminMenuGrants } from '@fiket/shared'
import { useAuth } from './auth'

/**
 * 메뉴 권한 훅 (FIKET-417) — /auth/me 의 role·adminGrants 를 @fiket/shared ADMIN_MENUS 기준으로 해석.
 * ADMIN 은 전부 true. MANAGER 는 그룹 grants 에 따라. 서버가 API 단계에서 강제하므로 이 훅은 UX 보조(네비 필터·버튼 숨김)다.
 */
export interface Permissions {
  isAdmin: boolean
  isManager: boolean
  /** 메뉴키 기준 보기 권한 */
  canView: (menuKey: string) => boolean
  /** 메뉴키 기준 편집 권한 — 변이 버튼 노출 여부 */
  canEdit: (menuKey: string) => boolean
  /** v2 경로 기준 보기 권한. 메뉴에 속하지 않는 경로(홈 등)는 항상 true */
  canViewPath: (pathname: string) => boolean
}

export function usePermissions(): Permissions {
  const { user } = useAuth()
  return useMemo(() => {
    const isAdmin = user?.role === 'ADMIN'
    const grants = (user?.adminGrants ?? null) as AdminMenuGrants | null
    const canView = (key: string) => isAdmin || canViewAdminMenu(grants, key)
    const canEdit = (key: string) => isAdmin || canEditAdminMenu(grants, key)
    const canViewPath = (pathname: string) => {
      const key = adminMenuKeyForNavPath(pathname)
      return key ? canView(key) : true
    }
    return { isAdmin, isManager: user?.role === 'MANAGER', canView, canEdit, canViewPath }
  }, [user])
}
